import { useMemo, useState } from "react";
import {
  useCategoryAttributesWithValues,
  useCreateCategoryAttributeWithValues,
  useUpdateCategoryAttributeWithValues,
  useDeleteCategoryAttributeWithValues,
} from "../../queries";
import { useCategories } from "../../queries";
import type {
  CategoryAttributeResponse,
  CreateCategoryAttributeRequest,
} from "@/services/api";
import {
  sortAttributes,
  calculateCategoriesLinked,
  type AttributeSortField,
  type AttributeWithCounts,
} from "./attributeUtils";
import { useAttributesSearchParams } from "./useAttributesSearchParams";

export function useAttributesPanel() {
  const { data: attributes, isLoading } = useCategoryAttributesWithValues();
  const { data: categories } = useCategories();
  const createMutation = useCreateCategoryAttributeWithValues();
  const updateMutation = useUpdateCategoryAttributeWithValues();
  const deleteMutation = useDeleteCategoryAttributeWithValues();

  const { sorting, updateSearchParams } = useAttributesSearchParams();
  const { sortField, sortDirection } = sorting;
  const [editingAttribute, setEditingAttribute] =
    useState<CategoryAttributeResponse | null>(null);
  const [deletingAttribute, setDeletingAttribute] =
    useState<CategoryAttributeResponse | null>(null);
  const [viewingCategoriesAttribute, setViewingCategoriesAttribute] =
    useState<CategoryAttributeResponse | null>(null);
  const [viewingValuesAttribute, setViewingValuesAttribute] =
    useState<CategoryAttributeResponse | null>(null);

  const attributesWithCounts = useMemo<AttributeWithCounts[]>(() => {
    if (!attributes) return [];

    return attributes.map((attr) => ({
      ...attr,
      categoriesLinked: calculateCategoriesLinked(attr.id, categories || []),
      valuesLinked: attr.values?.length || 0,
    }));
  }, [attributes, categories]);

  const sortedAttributes = useMemo<AttributeWithCounts[]>(() => {
    if (attributesWithCounts.length === 0) return [];
    return sortAttributes(attributesWithCounts, sortField, sortDirection);
  }, [attributesWithCounts, sortField, sortDirection]);

  const handleSort = (field: AttributeSortField) => {
    if (sortField === field) {
      updateSearchParams({
        sortField: field,
        sortDirection: sortDirection === "asc" ? "desc" : "asc",
      });
    } else {
      updateSearchParams({
        sortField: field,
        sortDirection: "asc",
      });
    }
  };

  const handleCreate = (data: CreateCategoryAttributeRequest) => {
    createMutation.mutate(data);
  };

  const handleUpdate = (
    id: string | number,
    data: CreateCategoryAttributeRequest
  ) => {
    updateMutation.mutate(
      { id, data },
      {
        onSuccess: () => {
          setEditingAttribute(null);
        },
      }
    );
  };

  const handleDelete = () => {
    if (!deletingAttribute) return;

    deleteMutation.mutate(deletingAttribute.id, {
      onSuccess: () => {
        setDeletingAttribute(null);
      },
    });
  };

  return {
    attributes: sortedAttributes,
    isLoading,
    sortField,
    sortDirection,
    editingAttribute,
    deletingAttribute,
    viewingCategoriesAttribute,
    viewingValuesAttribute,
    handleSort,
    handleCreate,
    handleUpdate,
    handleDelete,
    setEditingAttribute,
    setDeletingAttribute,
    setViewingCategoriesAttribute,
    setViewingValuesAttribute,
    createIsLoading: createMutation.isPending,
    updateIsLoading: updateMutation.isPending,
    deleteIsLoading: deleteMutation.isPending,
    attributesCount: attributes?.length,
  };
}
